import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../api/api';
import { ArrowLeft } from '@phosphor-icons/react';
import PendingRequestBanner from '../components/PendingRequestBanner';
import { readPendingRequest } from '../components/PendingRequestSubmitter';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import PasswordStrengthIndicator, { validatePassword } from '../components/PasswordStrengthIndicator';
import { toast } from 'sonner';

const RegisterPage = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('user');
  const [loading, setLoading] = useState(false);
  const [searchParams] = useSearchParams();
  const pendingEmail = readPendingRequest()?.payload?.contact_email || '';
  const { login } = useAuth();
  const navigate = useNavigate();

  // L'email della richiesta inviata dalla landing non è modificabile
  useEffect(() => {
    if (pendingEmail) setEmail(pendingEmail);
  }, [pendingEmail]);

  const handleRegister = async (e) => {
    e.preventDefault();
    const { isValid } = validatePassword(password);
    if (!isValid) {
      toast.error('La password non rispetta i requisiti di sicurezza');
      return;
    }
    setLoading(true);

    try {
      const response = await api.post('/api/auth/register', {
        name,
        email,
        password,
        role,
      });
      login(response.data.token, response.data.user);
      if (role === 'professional') {
        toast.success('Account creato! Completa il profilo professionale.');
        navigate('/professional/completa-profilo', { replace: true });
      } else {
        toast.success('Registrazione completata!');
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="px-4 pt-6">
        <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-foreground/60 hover:text-primary transition-colors" data-testid="back-home-link">
          <ArrowLeft size={16} /> Torna alla home
        </Link>
      </div>

      <div className="flex items-center justify-center px-4 py-16">
        <div className="w-full max-w-md">
          <PendingRequestBanner mode="register" />
          <Card className="w-full p-8 neumorphism rounded-2xl" data-testid="register-form">
          <div className="text-center mb-8">
            <img src="/vivalink-logo-transparent.png" alt="VivaLink Italia" className="h-20 w-auto mx-auto mb-5" />
            <h1 className="font-heading font-semibold text-3xl text-foreground mb-2">Crea il Tuo Account</h1>
            <p className="text-foreground/70">Unisciti alla community VivaLink Italia</p>
          </div>

          <form onSubmit={handleRegister} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Completo</Label>
              <Input
                id="name"
                type="text"
                placeholder="Mario Rossi"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                data-testid="name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="mario.rossi@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                readOnly={!!pendingEmail}
                className={pendingEmail ? 'bg-muted/60 cursor-not-allowed' : ''}
                data-testid="email-input"
              />
              {pendingEmail && (
                <p className="text-xs text-foreground/60" data-testid="email-locked-note">
                  È l'email indicata nella richiesta: non può essere modificata.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                data-testid="password-input"
              />
              <PasswordStrengthIndicator password={password} />
            </div>

            <div className="space-y-2">
              <Label htmlFor="role">Tipo di Account</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger data-testid="role-select">
                  <SelectValue placeholder="Seleziona tipo di account" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="user">Utente / Famiglia</SelectItem>
                  <SelectItem value="professional">Professionista Sanitario</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button
              type="submit"
              className="w-full rounded-full"
              disabled={loading}
              data-testid="register-submit-btn"
            >
              {loading ? 'Registrazione in corso...' : 'Registrati'}
            </Button>
          </form>

          <div className="text-center mt-6">
            <p className="text-sm text-foreground/70">
              Hai già un account?{' '}
              <Link to="/login" className="text-primary font-medium hover:underline" data-testid="login-link">
                Accedi
              </Link>
            </p>
            <div className="mt-4 pt-4 border-t border-border/50 space-y-2">
              <p className="text-xs text-foreground/60">Sei un professionista?</p>
              <div className="flex gap-2 justify-center flex-wrap">
                <Link to="/register-professional" className="text-xs text-primary underline" data-testid="register-pro-link">
                  Registrati come Professionista
                </Link>
                <span className="text-foreground/40">·</span>
                <Link to="/register-centro" className="text-xs text-primary underline" data-testid="register-centro-link">
                  Registra un Centro di Assistenza
                </Link>
              </div>
            </div>
          </div>
        </Card>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;